// frontend/src/components/BatchOptimization.tsx

import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useSnackbar } from 'notistack';
import {
  Alert, Box, Typography, Paper, Button, Stack, Table, TableBody, Select, MenuItem,
  TableCell, TableContainer, TableHead, TableRow, LinearProgress, InputLabel,
  Chip, Tooltip, CircularProgress, FormControl, SelectChangeEvent,
  alpha, useTheme, Grid, Dialog, DialogActions, DialogContent, DialogTitle
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import DescriptionIcon from '@mui/icons-material/Description';
import TuneIcon from '@mui/icons-material/Tune'; // Icono para slider/ajustes
import { Slider } from '@mui/material';

import { uploadBatchFile, getJobsList, getJobStatus, downloadJobResult, deleteJob, reOptimizeJob } from '../services/batchService';
import type { BatchJob } from '../services/batchService';
import {
  analyzePricingFileNormalization,
  reviewPricingFileNormalization,
} from '../services/fileNormalizationService';
import type { FileNormalizationAnalyzeResult } from '../types/fileNormalization';
import Papa from 'papaparse';
import ConfirmDialog from '../modules/pricingJourney/components/ConfirmDialog';

// PRC-033: acciones de fila con nombre, del mismo tono.
const rowActionSx = { textTransform: 'none', fontWeight: 700, color: '#cbd5e1', minWidth: 0, px: 1, '&:hover': { color: '#f8fafc', bgcolor: 'rgba(148,163,184,0.08)' } };

// --- ESTILOS DARK PREMIUM ---
const glassCardSx = {
    bgcolor: '#1e293b', 
    border: '1px solid #334155',
    borderRadius: 2,
    p: 3, // Reducido de 4 a 3 para ser más compacto
    height: 'auto',
    width: '100%',
    display: 'flex', 
    flexDirection: 'column',
    boxShadow: 'none'
};

// ESTA es la clave: height 100% para llenar la columna izquierda
const uploadZoneSx = {
    border: '1px dashed #334155',
    borderRadius: 2,
    p: 3,
    textAlign: 'center',
    cursor: 'pointer',
    bgcolor: 'rgba(255, 255, 255, 0.02)',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',           // Flexbox activado
    flexDirection: 'column',   // Elementos en columna
    alignItems: 'center',
    justifyContent: 'center',  // Centrado vertical
    height: '100%',            // OCUPA TODO EL ALTO DISPONIBLE
    minHeight: '250px',        // Altura mínima de seguridad compacta
    gap: 2,
    '&:hover': {
        bgcolor: 'rgba(56, 189, 248, 0.05)',
        borderColor: '#38bdf8'
    }
};

const stealthSelectSx = {
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-select': { 
        bgcolor: 'rgba(255, 255, 255, 0.03)', 
        border: '1px solid #334155',
        borderRadius: 1,
        py: 1.5,
        fontSize: '0.95rem',
        color: '#f8fafc',
        '&:focus': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
    },
    '&:hover .MuiSelect-select': { borderColor: '#64748b' },
    '& .MuiSelect-icon': { color: '#64748b' },
    '& .MuiInputLabel-root': { color: '#94a3b8' }, 
    '& .MuiInputLabel-root.Mui-focused': { color: '#38bdf8' }
};

const POLLING_INTERVAL = 5000;

const BatchOptimization: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  
  // --- STATE ---
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState({ customer_id: '', product_id: '' });

  const [normalization, setNormalization] = useState<FileNormalizationAnalyzeResult | null>(null);
  const [normalizationSheetName, setNormalizationSheetName] = useState<string | null>(null);
  const [normalizationHeaderRowInput, setNormalizationHeaderRowInput] = useState<string>('1');
  const [normalizationReviewOpen, setNormalizationReviewOpen] = useState<boolean>(false);
  const [normalizationConfirmed, setNormalizationConfirmed] = useState<boolean>(false);
  const [normalizationError, setNormalizationError] = useState<string | null>(null);
  
  const [jobs, setJobs] = useState<BatchJob[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isProcessingFile, setIsProcessingFile] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  // PRC-032: borrar una ejecución se confirma antes.
  const [confirmDeleteJobId, setConfirmDeleteJobId] = useState<string | null>(null);
  const [reOptimizingJobId, setReOptimizingJobId] = useState<string | null>(null);

  // --- ESTADO ESTRATEGIA (V2.0) ---
  const [strategyProfile, setStrategyProfile] = useState<string>('equilibrado');
  const [strategySensitivity, setStrategySensitivity] = useState<number>(50);

  // Handlers
  const handleStrategyPreset = (profile: string) => {
    setStrategyProfile(profile);
    if (profile === 'conservador') setStrategySensitivity(25);
    if (profile === 'equilibrado') setStrategySensitivity(50);
    if (profile === 'agresivo') setStrategySensitivity(90);
  };

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    setStrategySensitivity(newValue as number);
    setStrategyProfile('manual');
  };

  // --- EFFECTS & HANDLERS ---
  const fetchJobs = useCallback(async () => { setIsLoading(true); try { const jobList = await getJobsList(); setJobs(jobList); } catch (err: any) { enqueueSnackbar(err.message || "No se pudo cargar el historial de trabajos.", { variant: 'error' }); } finally { setIsLoading(false); } }, [enqueueSnackbar]);
  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => { const activeJobs = jobs.filter(j => j.status === 'PENDING' || j.status === 'RUNNING'); if (activeJobs.length === 0) return; const intervalId = setInterval(() => { Promise.all(jobs.map(j => (j.status === 'PENDING' || j.status === 'RUNNING') ? getJobStatus(j.job_id).catch(() => ({ ...j, status: 'FAILED', error_message: 'Fallo al actualizar.' } as BatchJob)) : Promise.resolve(j))).then(setJobs); }, POLLING_INTERVAL); return () => clearInterval(intervalId); }, [jobs]);

  const resetFileState = () => {
    setSelectedFile(null);
    setFileHeaders([]);
    setMapping({ customer_id: '', product_id: '' });
    setNormalization(null);
    setNormalizationSheetName(null);
    setNormalizationHeaderRowInput('1');
    setNormalizationReviewOpen(false);
    setNormalizationConfirmed(false);
    setNormalizationError(null);

    const fileInput = document.getElementById('file-input-batch') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const readCsvHeaders = (fileToRead: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          Papa.parse(event.target?.result as string, {
            header: true,
            preview: 1,
            complete: (results) => resolve(results.meta.fields || []),
            error: (error: Error) => reject(new Error(`Error al parsear CSV: ${error.message}`)),
          });
        } catch (parseError: any) {
          reject(new Error(`Fallo crítico durante el procesamiento del archivo: ${parseError.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Error del sistema al leer el archivo.'));
      reader.readAsText(fileToRead);
    });
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      resetFileState();
      return;
    }

    const fileName = file.name.toLowerCase();

    setIsProcessingFile(true);
    setSelectedFile(file);
    setFileHeaders([]);
    setMapping({ customer_id: '', product_id: '' });
    setNormalization(null);
    setNormalizationSheetName(null);
    setNormalizationHeaderRowInput('1');
    setNormalizationReviewOpen(false);
    setNormalizationConfirmed(false);
    setNormalizationError(null);

    try {
      if (fileName.endsWith('.csv')) {
        const headers = await readCsvHeaders(file);

        if (headers.length === 0) {
          throw new Error('El archivo parece estar vacío o no contiene cabeceras.');
        }

        setFileHeaders(headers);
        return;
      }

      if (!fileName.endsWith('.xlsx')) {
        throw new Error('Tipo de archivo no soportado.');
      }

      const normalizationResult = await analyzePricingFileNormalization(
        file,
        'batch_optimization',
      );

      setNormalization(normalizationResult);
      setNormalizationSheetName(normalizationResult.selected_sheet);
      setNormalizationHeaderRowInput(String(normalizationResult.header_row_index + 1));
      setNormalizationReviewOpen(true);
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.detail || err.message || 'Error desconocido al procesar el archivo.',
        { variant: 'error' },
      );
      resetFileState();
    } finally {
      setIsProcessingFile(false);
    }
  };

  const parsedNormalizationHeaderRow = Number(normalizationHeaderRowInput);
  const draftNormalizationHeaderRowIndex =
    Number.isInteger(parsedNormalizationHeaderRow) && parsedNormalizationHeaderRow >= 1
      ? parsedNormalizationHeaderRow - 1
      : null;

  const hasPendingNormalizationReview = Boolean(
    normalization &&
    (
      normalizationSheetName !== normalization.selected_sheet ||
      draftNormalizationHeaderRowIndex !== normalization.header_row_index
    ),
  );

  const handleNormalizationSheetChange = (newSheetName: string) => {
    setNormalizationSheetName(newSheetName);
    setNormalizationError(null);
  };

  const handleApplyNormalizationReview = async () => {
    if (!selectedFile || !normalization) return;

    if (draftNormalizationHeaderRowIndex === null) {
      setNormalizationError('La fila de cabecera debe ser un número entero mayor o igual que 1.');
      return;
    }

    const sheetName = normalizationSheetName || normalization.selected_sheet;

    setIsProcessingFile(true);
    setNormalizationError(null);

    try {
      const reviewResult = await reviewPricingFileNormalization(
        selectedFile,
        'batch_optimization',
        draftNormalizationHeaderRowIndex,
        sheetName,
      );

      setNormalization(reviewResult);
      setNormalizationSheetName(reviewResult.selected_sheet);
      setNormalizationHeaderRowInput(String(reviewResult.header_row_index + 1));
      setNormalizationConfirmed(false);
    } catch (err: any) {
      const detail =
        err.response?.data?.detail ||
        err.message ||
        'No se pudo aplicar la revisión estructural del archivo.';

      setNormalizationError(detail);
      enqueueSnackbar(detail, { variant: 'error' });
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleConfirmNormalization = () => {
    if (!normalization || hasPendingNormalizationReview) return;

    if (!normalization.selected_sheet) {
      setNormalizationError('No se ha podido determinar la hoja activa del archivo Excel.');
      return;
    }

    if (normalization.detected_columns.length === 0) {
      setNormalizationError('La configuración seleccionada no contiene columnas detectables.');
      return;
    }

    setFileHeaders(normalization.detected_columns);
    setMapping((previousMapping) => ({
      customer_id: normalization.detected_columns.includes(previousMapping.customer_id)
        ? previousMapping.customer_id
        : '',
      product_id: normalization.detected_columns.includes(previousMapping.product_id)
        ? previousMapping.product_id
        : '',
    }));
    setNormalizationConfirmed(true);
    setNormalizationReviewOpen(false);
    setNormalizationError(null);
  };

  const handleCloseNormalizationReview = () => {
    if (isProcessingFile) return;

    if (normalizationConfirmed && normalization) {
      setNormalizationSheetName(normalization.selected_sheet);
      setNormalizationHeaderRowInput(String(normalization.header_row_index + 1));
      setNormalizationError(null);
      setNormalizationReviewOpen(false);
      return;
    }

    resetFileState();
  };


  const handleMappingChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (name) { setMapping(prev => ({ ...prev, [name]: value as string })); }
  };

  const handleUpload = async () => {
    if (!selectedFile || !mapping.customer_id || !mapping.product_id) return;

    const isExcelFile = selectedFile.name.toLowerCase().endsWith('.xlsx');

    if (isExcelFile && (!normalizationConfirmed || !normalization?.selected_sheet)) {
      enqueueSnackbar(
        'Confirma primero la hoja y la fila de cabecera del archivo Excel.',
        { variant: 'warning' },
      );
      return;
    }

    setIsUploading(true);

    try {
      const config = {
        strategy_profile: strategyProfile,
        strategy_sensitivity: strategySensitivity,
        sheet_name: isExcelFile ? normalization?.selected_sheet ?? null : null,
        header_row_index: isExcelFile ? normalization?.header_row_index ?? null : null,
      };
      
      const response = await uploadBatchFile(selectedFile, mapping, config);
      
      enqueueSnackbar(`Trabajo ${response.job_id} iniciado con éxito.`, { variant: 'success' });
      resetFileState();
      fetchJobs();
    } catch (err: any) {
      enqueueSnackbar(err.message, { variant: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (jobId: string) => {
    setDownloadingJobId(jobId);
    try { await downloadJobResult(jobId); } 
    catch (err: any) { enqueueSnackbar(err.message, { variant: 'error' }); } 
    finally { setDownloadingJobId(null); }
  };
  
  const handleDelete = async (jobId: string) => {
    setConfirmDeleteJobId(null);
    setDeletingJobId(jobId);
    try { await deleteJob(jobId); setJobs(prevJobs => prevJobs.filter(job => job.job_id !== jobId)); enqueueSnackbar("Trabajo eliminado correctamente.", { variant: 'info' }); } 
    catch (err: any) { enqueueSnackbar(err.message, { variant: 'error' }); } 
    finally { setDeletingJobId(null); }
  };

  const handleReOptimize = async (jobId: string) => {
    setReOptimizingJobId(jobId);
    try { const response = await reOptimizeJob(jobId); enqueueSnackbar(`Nuevo trabajo ${response.job_id} iniciado para re-optimización.`, { variant: 'success' }); fetchJobs(); } 
    catch (err: any) { enqueueSnackbar(err.message, { variant: 'error' }); } 
    finally { setReOptimizingJobId(null); }
  };

  const renderStatusChip = (status: BatchJob['status']) => {
    const statusMap: any = {
      PENDING: { label: 'Pendiente', color: 'warning', icon: <PendingIcon fontSize="small" /> },
      RUNNING: { label: 'Procesando', color: 'info', icon: <AutorenewIcon className="rotating" fontSize="small" /> },
      COMPLETED: { label: 'Completado', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
      COMPLETED_WITH_ERRORS: { label: 'Alertas', color: 'warning', icon: <ErrorIcon fontSize="small" /> },
      FAILED: { label: 'Fallido', color: 'error', icon: <ErrorIcon fontSize="small" /> },
    };
    const { label, color, icon } = statusMap[status] || { label: 'Desc', color: 'default', icon: <ErrorIcon /> };
    
    return (
        <Chip 
            icon={icon} 
            label={label} 
            size="small" 
            sx={{ 
                bgcolor: alpha(theme.palette[color as 'success'|'warning'|'error'|'info'].main, 0.1),
                color: theme.palette[color as 'success'|'warning'|'error'|'info'].main,
                borderColor: alpha(theme.palette[color as 'success'|'warning'|'error'|'info'].main, 0.2),
                border: '1px solid',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'inherit' }
            }} 
        />
    );
  };

  const isMappingComplete = mapping.customer_id !== '' && mapping.product_id !== '';
  const isSelectedExcelFile = selectedFile?.name.toLowerCase().endsWith('.xlsx') ?? false;
  const canExecute =
    isMappingComplete &&
    !isUploading &&
    !isProcessingFile &&
    (!isSelectedExcelFile || normalizationConfirmed);
  
  return (
    <Box sx={{ mb: 8 }}>
      {/* Revisión estructural XLSX */}
      <Dialog
        open={normalizationReviewOpen && Boolean(selectedFile && normalization)}
        fullWidth
        maxWidth="lg"
        onClose={handleCloseNormalizationReview}
        PaperProps={{
          sx: {
            bgcolor: '#0f172a',
            border: '1px solid #334155',
            borderRadius: 3,
            maxHeight: '88vh',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#f8fafc',
            fontWeight: 800,
            borderBottom: '1px solid #334155',
            px: 3,
            py: 2.25,
          }}
        >
          Configurar archivo Excel
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            bgcolor: '#0f172a',
            borderColor: '#334155',
            px: { xs: 2, md: 3 },
            py: 3,
          }}
        >
          {normalization && (
            <Stack spacing={2}>
              {normalizationError && (
                <Alert
                  severity="error"
                  variant="outlined"
                  sx={{ borderColor: '#ef4444', color: '#fca5a5' }}
                >
                  {normalizationError}
                </Alert>
              )}

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" sx={{ color: '#38bdf8', fontWeight: 700 }}>
                  Configuración detectada
                </Typography>

                <Chip
                  size="small"
                  label={normalization.confidence === 'high' ? 'Detección automática' : 'Revisión recomendada'}
                  color={normalization.confidence === 'high' ? 'success' : 'warning'}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Stack>

              {normalization.sheets.length > 1 && (
                <Stack spacing={0.75}>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Hoja activa
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {normalization.sheets.map((sheet) => (
                      <Chip
                        key={sheet.name}
                        label={`${sheet.name} (${sheet.row_count} filas)`}
                        size="small"
                        onClick={() => handleNormalizationSheetChange(sheet.name)}
                        color={normalizationSheetName === sheet.name ? 'primary' : 'default'}
                        variant={normalizationSheetName === sheet.name ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}

              <Stack spacing={1}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="caption" sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    Fila de cabecera
                  </Typography>

                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 28, px: 0.5, borderColor: '#334155', color: '#94a3b8' }}
                      disabled={
                        isProcessingFile ||
                        draftNormalizationHeaderRowIndex === null ||
                        draftNormalizationHeaderRowIndex <= 0
                      }
                      onClick={() => {
                        const currentIndex = draftNormalizationHeaderRowIndex ?? 0;
                        setNormalizationHeaderRowInput(String(Math.max(0, currentIndex - 1) + 1));
                        setNormalizationError(null);
                      }}
                    >
                      -
                    </Button>

                    <Typography sx={{ color: '#f8fafc', minWidth: 24, textAlign: 'center', fontSize: '0.9rem' }}>
                      {normalizationHeaderRowInput}
                    </Typography>

                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 28, px: 0.5, borderColor: '#334155', color: '#94a3b8' }}
                      disabled={isProcessingFile}
                      onClick={() => {
                        const currentIndex = draftNormalizationHeaderRowIndex ?? 0;
                        setNormalizationHeaderRowInput(String(currentIndex + 2));
                        setNormalizationError(null);
                      }}
                    >
                      +
                    </Button>
                  </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isProcessingFile || !hasPendingNormalizationReview}
                    onClick={() => void handleApplyNormalizationReview()}
                    sx={{
                      alignSelf: 'flex-start',
                      borderColor: '#38bdf8',
                      color: '#38bdf8',
                      '&:hover': {
                        borderColor: '#67e8f9',
                        bgcolor: 'rgba(56, 189, 248, 0.06)',
                      },
                    }}
                  >
                    {isProcessingFile ? 'Aplicando...' : 'Aplicar revisión'}
                  </Button>

                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {hasPendingNormalizationReview
                      ? 'Selección local pendiente de aplicar.'
                      : 'Configuración aplicada.'}
                  </Typography>
                </Stack>
              </Stack>

              {normalization.raw_preview_rows.length > 0 && (
                <Stack spacing={0.75}>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Vista previa cruda de la hoja
                  </Typography>

                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                    La fila resaltada se actualiza localmente. Pulse “Aplicar revisión” únicamente cuando la selección sea correcta.
                  </Typography>

                  {normalizationSheetName !== normalization.selected_sheet && (
                    <Alert
                      severity="info"
                      variant="outlined"
                      sx={{
                        py: 0.25,
                        borderColor: 'rgba(56, 189, 248, 0.35)',
                        color: '#bae6fd',
                        '& .MuiAlert-icon': { color: '#38bdf8' },
                      }}
                    >
                      Aplique la revisión para cargar el contenido de la nueva hoja seleccionada.
                    </Alert>
                  )}

                  <TableContainer
                    sx={{
                      maxHeight: 260,
                      overflowX: 'auto',
                      border: '1px solid #334155',
                      borderRadius: 1.5,
                      bgcolor: 'rgba(2, 6, 23, 0.22)',
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableBody>
                        {normalization.raw_preview_rows.slice(0, 12).map((row, rowIndex) => {
                          const isCandidateHeader = draftNormalizationHeaderRowIndex === rowIndex;

                          return (
                            <TableRow
                              key={rowIndex}
                              sx={{
                                bgcolor: isCandidateHeader
                                  ? 'rgba(56, 189, 248, 0.12)'
                                  : 'transparent',
                              }}
                            >
                              <TableCell
                                sx={{
                                  minWidth: 86,
                                  color: isCandidateHeader ? '#38bdf8' : '#64748b',
                                  fontWeight: isCandidateHeader ? 800 : 500,
                                  fontSize: '0.7rem',
                                  borderColor: '#1e293b',
                                  whiteSpace: 'nowrap',
                                  py: 0.5,
                                  px: 1,
                                }}
                              >
                                {isCandidateHeader ? 'CABECERA' : `Fila ${rowIndex + 1}`}
                              </TableCell>

                              {row.map((cell, cellIndex) => (
                                <TableCell
                                  key={cellIndex}
                                  sx={{
                                    color: isCandidateHeader ? '#e2e8f0' : '#94a3b8',
                                    fontWeight: isCandidateHeader ? 700 : 400,
                                    fontSize: '0.72rem',
                                    borderColor: '#1e293b',
                                    whiteSpace: 'nowrap',
                                    py: 0.5,
                                    px: 1,
                                  }}
                                >
                                  {String(cell ?? '').substring(0, 26)}
                                  {String(cell ?? '').length > 26 ? '…' : ''}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )}

              <Button
                size="small"
                variant="contained"
                disabled={isProcessingFile || hasPendingNormalizationReview}
                sx={{
                  bgcolor: '#38bdf8',
                  color: '#0f172a',
                  fontWeight: 700,
                  alignSelf: 'flex-start',
                  '&:hover': { bgcolor: '#0ea5e9' },
                }}
                onClick={handleConfirmNormalization}
              >
                Usar esta configuración
              </Button>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: '#0f172a',
            borderTop: '1px solid #334155',
            px: 3,
            py: 1.75,
          }}
        >
          <Button
            variant="text"
            disabled={isProcessingFile}
            onClick={handleCloseNormalizationReview}
            sx={{ color: '#94a3b8' }}
          >
            {normalizationConfirmed ? 'Cerrar' : 'Cancelar archivo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. Upload & Mapping Section */}
      <Paper elevation={0} sx={glassCardSx}>
        {/* alignItems="stretch" es lo que obliga a la izquierda a medir lo mismo que la derecha */}
        <Grid container spacing={4} alignItems="stretch">
            
            {/* IZQUIERDA: ZONA DE CARGA (Se estirará automáticamente) */}
            <Grid item xs={12} lg={5} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 1, fontWeight: 700, fontSize: '0.75rem' }}>
                    1. Archivo con tus productos
                </Typography>
                
                <Box component="label" sx={uploadZoneSx}>
                    {isProcessingFile ? (
                        <CircularProgress size={30} sx={{ color: '#38bdf8' }} />
                    ) : selectedFile ? (
                        // ESTADO: ARCHIVO CARGADO + ANÁLISIS
                        <>
                            <CheckCircleIcon sx={{ fontSize: 40, color: '#4ade80' }} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                                    {selectedFile.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                                    {(selectedFile.size / 1024).toFixed(0)} KB
                                </Typography>
                            </Box>

                            {/* NUEVO: Visualización de Columnas Detectadas */}
                            {fileHeaders.length > 0 && (
                                <Box sx={{ mt: 1, width: '100%' }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                        {fileHeaders.length} columnas detectadas:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', maxHeight: '100px', overflowY: 'auto' }}>
                                        {fileHeaders.slice(0, 8).map(h => (
                                            <Chip key={h} label={h} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.05)', color: '#94a3b8' }} />
                                        ))}
                                        {fileHeaders.length > 8 && <Chip label={`+${fileHeaders.length - 8}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                                    </Box>
                                </Box>
                            )}

                            {normalizationConfirmed && normalization && (
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="center" sx={{ mt: 1 }}>
                                    <Chip
                                        size="small"
                                        label={`Hoja: ${normalization.selected_sheet ?? 'N/A'}`}
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.65rem', borderColor: '#334155', color: '#94a3b8' }}
                                    />
                                    <Chip
                                        size="small"
                                        label={`Cabecera: fila ${normalization.header_row_index + 1}`}
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.65rem', borderColor: '#334155', color: '#94a3b8' }}
                                    />
                                </Stack>
                            )}

                            {normalizationConfirmed && normalization && (
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setNormalizationReviewOpen(true);
                                    }}
                                    sx={{ mt: 0.5, color: '#38bdf8', fontSize: '0.72rem' }}
                                >
                                    Ajustar configuración
                                </Button>
                            )}

                            <Button size="small" color="error" onClick={(e) => { e.preventDefault(); resetFileState(); }} sx={{ mt: 1 }}>
                                Eliminar
                            </Button>
                        </>
                    ) : (
                        // ESTADO: VACÍO
                        <>
                            <CloudUploadIcon sx={{ fontSize: 40, color: '#334155' }} />
                            <Box>
                                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                                    Subir Excel o CSV
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Arrastra o haz clic
                                </Typography>
                            </Box>
                        </>
                    )}
                    <input id="file-input-batch" type="file" hidden onChange={handleFileChange} accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={isUploading || isProcessingFile} />
                </Box>
            </Grid>

            {/* DERECHA: CONFIGURACIÓN (Define la altura natural) */}
            <Grid item xs={12} lg={7}>
                <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 1, fontWeight: 700, fontSize: '0.75rem' }}>
                    2. Columnas y estrategia
                </Typography>

                <Stack spacing={2}> {/* Stack más compacto (spacing 2 en vez de 3) */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth size="small" sx={stealthSelectSx}>
                            <InputLabel>Columna ID Cliente *</InputLabel>
                            <Select
                                label="Columna ID Cliente *"
                                name="customer_id"
                                value={mapping.customer_id}
                                onChange={handleMappingChange}
                                disabled={!selectedFile || fileHeaders.length === 0}
                            >
                                {fileHeaders.map(header => <MenuItem key={header} value={header}>{header}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={stealthSelectSx}>
                            <InputLabel>Columna ID Producto *</InputLabel>
                            <Select
                                label="Columna ID Producto *"
                                name="product_id"
                                value={mapping.product_id}
                                onChange={handleMappingChange}
                                disabled={!selectedFile || fileHeaders.length === 0}
                            >
                                {fileHeaders.map(header => <MenuItem key={header} value={header}>{header}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Selector de Estrategia Compacto */}
                    <Box sx={{ p: 2, border: '1px solid #334155', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
                        {/* ... (Mantén aquí tu código del Slider/Botones actual) ... */}
                        {/* Solo asegúrate de reducir margins (mb: 1 o mb: 2) para compactar */}
                        
                        {/* COPIA AQUÍ DENTRO EL BLOQUE "ESTRATEGIA DE IA" QUE YA TIENES EN TU ARCHIVO ORIGINAL 
                            PERO REDUCIENDO LOS PADDINGS */}
                         <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            <TuneIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                            <Typography variant="caption" sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.8rem' }}>
                                Estrategia · {strategySensitivity} %
                            </Typography>
                        </Stack>
                        
                        {/* Botones Presets Compactos */}
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                             {/* ... Tus chips existentes ... */}
                             {/* PRC-033: opciones sin color propio; la elegida, en azul. */}
                             {[
                                { id: 'conservador', label: 'Conservadora' },
                                { id: 'equilibrado', label: 'Equilibrada' },
                                { id: 'agresivo', label: 'Agresiva' },
                            ].map((preset) => (
                                <Chip
                                    key={preset.id}
                                    label={preset.label}
                                    onClick={() => handleStrategyPreset(preset.id)}
                                    variant={strategyProfile === preset.id ? "filled" : "outlined"}
                                    size="small"
                                    sx={{
                                        borderColor: strategyProfile === preset.id ? '#38bdf8' : '#334155',
                                        color: strategyProfile === preset.id ? '#0f172a' : '#cbd5e1',
                                        bgcolor: strategyProfile === preset.id ? '#38bdf8' : 'transparent',
                                        '&:hover': { bgcolor: strategyProfile === preset.id ? '#7dd3fc' : alpha('#38bdf8', 0.08) },
                                        fontWeight: 700
                                    }}
                                />
                            ))}
                        </Stack>

                        <Slider
                            value={strategySensitivity}
                            onChange={handleSliderChange}
                            step={1} min={0} max={100}
                            size="small" // SLIDER PEQUEÑO
                            sx={{ color: '#38bdf8', height: 4, mb: 0 }}
                        />
                    </Box>

                    <Button 
                        variant="contained" 
                        fullWidth
                        onClick={handleUpload} 
                        disabled={!canExecute}
                        sx={{ 
                            bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700, 
                            '&:hover': { bgcolor: '#0ea5e9' },
                            textTransform: 'none',
                            height: '40px' // Altura controlada
                        }}
                    >
                        {isUploading ? 'Calculando…' : 'Calcular precios'}
                    </Button>
                </Stack>
            </Grid>
        </Grid>
      </Paper>

      {/* 3. History Table */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#f8fafc', fontWeight: 700, mb: 3 }}>
            3. Historial de ejecuciones
        </Typography>
        
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #334155', borderRadius: 2, bgcolor: '#1e293b' }}>
          <Table size="medium">
            <TableHead>
                <TableRow sx={{ bgcolor: '#0f172a' }}>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 700, borderBottom: '1px solid #334155' }}>Archivo</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 700, borderBottom: '1px solid #334155' }}>Estado</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 700, borderBottom: '1px solid #334155' }}>Resultado</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 700, borderBottom: '1px solid #334155' }}>Fecha</TableCell>
                    <TableCell align="center" sx={{ color: '#94a3b8', fontWeight: 700, borderBottom: '1px solid #334155' }}>Acciones</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? ( 
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}><CircularProgress sx={{ color: '#38bdf8' }} /></TableCell></TableRow> 
              ) : jobs.length === 0 ? ( 
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: '#64748b' }}>No hay trabajos recientes.</TableCell></TableRow> 
              ) : (
                jobs.map((job) => (
                  <TableRow key={job.job_id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }, borderBottom: '1px solid #334155' }}>
                    <TableCell sx={{ color: '#e2e8f0', borderColor: '#334155' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <DescriptionIcon fontSize="small" sx={{ color: '#64748b' }} />
                            <Tooltip title={job.input_file_path.split('/').pop()}>
                                <Typography variant="body2">{job.input_file_path.split('/').pop()?.substring(37) || 'Archivo'}</Typography>
                            </Tooltip>
                        </Box>
                    </TableCell>
                    <TableCell sx={{ borderColor: '#334155' }}>{renderStatusChip(job.status)}</TableCell>
                    <TableCell sx={{ borderColor: '#334155' }}>
                      {(() => {
                        // FIX: Safe access to total_rows using null coalescing
                        const totalRows = job.total_rows ?? 0;
                        const processedRows = job.processed_rows ?? 0;

                        if (job.status === 'RUNNING') {
                          return (
                            <Box sx={{ width: '100%', minWidth: 100 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">{Math.round(totalRows > 0 ? (processedRows / totalRows) * 100 : 0)}%</Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={totalRows > 0 ? (processedRows / totalRows) * 100 : 0} sx={{ bgcolor: 'rgba(56,189,248,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#38bdf8' }, height: 6, borderRadius: 1 }} />
                            </Box>
                          );
                        }
                        if (job.status.includes('COMPLETED')) {
                          return <Typography variant="body2" sx={{ color: '#4ade80' }}>{totalRows} filas</Typography>;
                        }
                        if (job.error_message) {
                            return <Tooltip title={job.error_message}><Typography variant="caption" sx={{ color: '#f87171', cursor: 'help', borderBottom: '1px dotted' }}>Ver error</Typography></Tooltip>;
                        }
                        return <Typography variant="caption" color="text.secondary">--</Typography>;
                      })()}
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#334155' }}>
                        {new Date(job.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute:'2-digit' })}
                    </TableCell>
                    <TableCell align="center" sx={{ borderColor: '#334155' }}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {/* PRC-033: acciones con nombre; el color de peligro solo en la confirmación. */}
                        {(job.status.includes('COMPLETED') || job.status === 'FAILED') && (
                          <Button size="small" onClick={() => handleReOptimize(job.job_id)} disabled={reOptimizingJobId === job.job_id} startIcon={<ReplayIcon fontSize="small" />} sx={rowActionSx}>
                            {reOptimizingJobId === job.job_id ? 'Repitiendo…' : 'Repetir'}
                          </Button>
                        )}
                        {(job.status.includes('COMPLETED')) && (
                          <Button size="small" onClick={() => handleDownload(job.job_id)} disabled={downloadingJobId === job.job_id} startIcon={<DownloadIcon fontSize="small" />} sx={rowActionSx}>
                            {downloadingJobId === job.job_id ? 'Descargando…' : 'Descargar'}
                          </Button>
                        )}
                        <Button size="small" onClick={() => setConfirmDeleteJobId(job.job_id)} disabled={deletingJobId === job.job_id} startIcon={<DeleteIcon fontSize="small" />} sx={rowActionSx}>
                          {deletingJobId === job.job_id ? 'Borrando…' : 'Borrar'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <ConfirmDialog
        open={!!confirmDeleteJobId}
        title="¿Borrar esta ejecución?"
        text="Se borran la ejecución y sus resultados descargables. No se puede deshacer."
        confirmLabel="Borrar"
        onCancel={() => setConfirmDeleteJobId(null)}
        onConfirm={() => { if (confirmDeleteJobId) void handleDelete(confirmDeleteJobId); }}
      />
      <style>{`.rotating { animation: spin 2s linear infinite; } @keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </Box>
  );
};

export default BatchOptimization;